// Chemin: C:\gestion-copro\src\pages\admin\MandatSignaturePage.tsx
// Page de signature du mandat de coordination - Gouvernance V2
// Session 88 : création initiale
// Référence : SPECIFICATION_GOUVERNANCE_V2.md §5.3
//
// Contexte : Cette page est affichée aux membres CS qui n'ont pas encore
// signé le mandat de coordination. Elle est déclenchée par ProtectedRoute
// quand checkSignatureRequise() retourne un mandatId.

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import {
  getMandatSignaturePageData,
  signerMandat,
  getMembreCSIdByUserId
} from '../../services/mandat.service';
import { MandatSignaturePageData } from '../../types/mandat.types';
import {
  Shield,
  FileSignature,
  CheckCircle2,
  AlertTriangle,
  Clock,
  User,
  Building2,
  ScrollText
} from 'lucide-react';

const MandatSignaturePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userProfile } = useAuth();
  const { coproprieteId } = useCopropriete();

  // État
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mandatData, setMandatData] = useState<MandatSignaturePageData | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [signatureComplete, setSignatureComplete] = useState(false);
  const [mandatActive, setMandatActive] = useState(false);

  const mandatId = searchParams.get('mandatId');

  // Chargement des données du mandat
  useEffect(() => {
    const loadMandat = async () => {
      if (!coproprieteId || !userProfile?.id) {
        setLoading(false);
        return;
      }

      try {
        // Récupérer le membreCSId de cet utilisateur
        const membreCSId = await getMembreCSIdByUserId(coproprieteId, userProfile.id);
        if (!membreCSId) {
          setError('Votre compte n\'est pas lié à un membre du Conseil Syndical.');
          setLoading(false);
          return;
        }

        // Récupérer les données du mandat
        const data = await getMandatSignaturePageData(coproprieteId, membreCSId);
        if (!data) {
          // Pas de mandat en attente ou déjà signé → rediriger
          navigate('/admin', { replace: true });
          return;
        }

        if (data.dejaSigne) {
          setSignatureComplete(true);
        }

        setMandatData(data);
      } catch (err) {
        console.error('Erreur chargement mandat:', err);
        setError('Impossible de charger le mandat de coordination.');
      } finally {
        setLoading(false);
      }
    };

    loadMandat();
  }, [coproprieteId, userProfile?.id, mandatId, navigate]);

  // Signature du mandat
  const handleSigner = async () => {
    if (!coproprieteId || !userProfile?.id || !mandatData) return;

    setSigning(true);
    setError(null);

    try {
      // Récupérer le membreCSId
      const membreCSId = await getMembreCSIdByUserId(coproprieteId, userProfile.id);
      if (!membreCSId) {
        setError('Impossible d\'identifier votre profil membre CS.');
        return;
      }

      // Récupérer l'IP (via service externe simple)
      let ip = 'non disponible';
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        ip = data.ip;
      } catch {
        // Si l'API IP échoue, on continue avec "non disponible"
      }

      // Signer le mandat
      const nom = `${userProfile.prenom} ${userProfile.nom}`;
      const toutSigne = await signerMandat(
        coproprieteId,
        mandatData.mandatId,
        membreCSId,
        userProfile.id,
        nom,
        ip
      );

      setSignatureComplete(true);
      setMandatActive(toutSigne);
    } catch (err: any) {
      console.error('Erreur signature:', err);
      setError(err.message || 'Erreur lors de la signature du mandat.');
    } finally {
      setSigning(false);
    }
  };

  // Continuer vers le dashboard
  const handleContinuer = () => {
    navigate('/admin', { replace: true });
  };

  // --- ÉCRANS ---

  // Chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du mandat...</p>
        </div>
      </div>
    );
  }

  // Erreur
  if (error && !mandatData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="text-red-500" size={32} />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Erreur</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/admin', { replace: true })}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }

  // Signature terminée
  if (signatureComplete) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="text-green-600" size={40} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Mandat signé
          </h1>
          <p className="text-gray-600 mb-2">
            Votre signature a été enregistrée avec succès.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Horodatage et adresse IP conservés comme preuve de signature.
          </p>

          {mandatActive && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-800 font-medium">
                Tous les membres ont signé — la copropriété est désormais opérationnelle.
              </p>
            </div>
          )}

          {!mandatActive && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 justify-center">
                <Clock className="text-amber-600" size={18} />
                <p className="text-amber-800 text-sm">
                  En attente des signatures des autres membres du Conseil Syndical.
                </p>
              </div>
            </div>
          )}

          <button
            onClick={handleContinuer}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Accéder à Copro Protect
          </button>
        </div>
      </div>
    );
  }

  // Pas de données mandat
  if (!mandatData) {
    return null;
  }

  // --- PAGE DE SIGNATURE PRINCIPALE ---
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        
        {/* En-tête */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Shield className="text-white" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Copro Protect</h1>
          <p className="text-gray-500 mt-1">Mandat de coordination</p>
        </div>

        {/* Carte d'information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="text-blue-600" size={20} />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">{mandatData.nomCopropriete}</h2>
              {mandatData.dateAG && (
                <p className="text-sm text-gray-500">
                  CS élu le {mandatData.dateAG.toDate().toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
              <User className="text-slate-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Coordinateur désigné</p>
              <p className="font-medium text-gray-900">{mandatData.coordinateurNom}</p>
              {mandatData.estMembreCS && (
                <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                  Membre élu du CS
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Texte du mandat */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <ScrollText className="text-gray-600" size={20} />
            <h3 className="font-semibold text-gray-900">Texte du mandat</h3>
          </div>

          <div className="bg-slate-50 rounded-lg p-5 max-h-96 overflow-y-auto border border-gray-100">
            <pre className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed font-sans">
              {mandatData.texteMandat}
            </pre>
          </div>
        </div>

        {/* Zone de signature */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <FileSignature className="text-gray-600" size={20} />
            <h3 className="font-semibold text-gray-900">Votre signature</h3>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            En signant ce mandat, vous confirmez la désignation de{' '}
            <span className="font-medium text-gray-900">{mandatData.coordinateurNom}</span>{' '}
            en qualité de Coordinateur/Coordinatrice et vous lui déléguez le pouvoir d'agir au nom 
            du Conseil Syndical dans le cadre de la gestion courante via Copro Protect.
          </p>

          <p className="text-sm text-gray-600 mb-6">
            Vous conservez un droit de consultation permanent et un pouvoir de blocage 
            sur les communications à engagement juridique (courriers classés ROUGE).
          </p>

          {/* Case à cocher */}
          <label className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50/30 transition-colors cursor-pointer mb-6">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
            <span className="text-sm text-gray-700">
              J'ai lu et compris le mandat de coordination ci-dessus. Je confirme la désignation 
              de <span className="font-medium">{mandatData.coordinateurNom}</span> comme 
              Coordinateur/Coordinatrice de la copropriété {mandatData.nomCopropriete}.
            </span>
          </label>

          {/* Erreur */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Bouton de signature */}
          <button
            onClick={handleSigner}
            disabled={!accepted || signing}
            className={`w-full py-3 rounded-lg font-medium text-white transition-colors flex items-center justify-center gap-2 ${
              accepted && !signing
                ? 'bg-blue-600 hover:bg-blue-700 shadow-sm'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {signing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Signature en cours...
              </>
            ) : (
              <>
                <FileSignature size={20} />
                Je confirme la désignation du coordinateur
              </>
            )}
          </button>

          <p className="text-xs text-gray-400 text-center mt-3">
            Votre signature sera horodatée et votre adresse IP enregistrée comme preuve.
          </p>
        </div>

        {/* Pied de page légal */}
        <p className="text-xs text-gray-400 text-center">
          Mandat de coordination — Base juridique : articles 1984 à 2010 du Code civil
          <br />
          Copro Protect — DOM TECH &amp; SERVICES
        </p>
      </div>
    </div>
  );
};

export default MandatSignaturePage;
